​import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, lastValueFrom, timeout, BehaviorSubject, throwError, of } from 'rxjs';
import { map, tap, catchError, switchMap, retryWhen, delay, take } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

import { AppConfigService } from '~/core/services/appconfig.service';
import { AppConfig } from '~/core/models/appconfig';
import { GraphService } from '~/core/services/graph.service';
import { ConversationsResponse, ConversationSummary } from '~/models';
import { Router } from '@angular/router';

/**
 * Lesson Report from Lookback Lessons API
 */
export interface LessonReport {
  well_id: string;
  well_name: string;
  report: string;
  metadata: {
    source_well: string;
    source_well_name: string;
    status: string;
    retrieval_strategy: string;
    npt_index_hits: number;
    asset_index_hits: number;
    similar_event_ids: string[];
    similar_well_ids: string[];
  };
}

/**
 * Response from Lookback Lessons API
 */
export interface LookbackLessonsResponse {
  status: string;
  well_ids: string[];
  reports: LessonReport[];
  summary: {
    total_wells_analyzed: number;
    successful_reports: number;
    failed_reports: number;
  };
  elapsed_seconds: number;
  timestamp: string;
}


const TIMEOUT: number = 300000; // 5 minutes

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    base: any;
    private get config(): AppConfig { return this.appconfigService.getConfig(); }
    private apiHeader: HttpHeaders = new HttpHeaders({ "accept": "text/plain", "Content-Type": "application/json" });
    private tokenSubject = new BehaviorSubject<string | null>(null);
    public token$ = this.tokenSubject.asObservable();
    private rolesLoaded = false;
    private rolesCache: any[] = [];

    constructor(
    private http: HttpClient,
    private appconfigService: AppConfigService,
    private msalService: MsalService,
    private graphService: GraphService,
    private router: Router
    ) {
        // Load existing token from localStorage
        const savedToken = localStorage.getItem('wells_api_token');
        if (savedToken) {
            this.tokenSubject.next(savedToken);
        } else {
        }
    }

    private getApiBaseUrl(): string {
        const url = (this.appconfigService.getApiUrl() || this.config.apiUrl || '').trim().replace(/\/+$/, '');
        if (!url) {
            throw new Error('API base URL is not configured. Please set assets/config.json -> apiUrl');
        }
        return url;
    }

    private buildApiUrl(path: string): string {
        const normalizedPath = `/${String(path || '').replace(/^\/+/, '')}`;
        return this.appconfigService.concatenateUrl(this.getApiBaseUrl(), normalizedPath);
    }

    public Get<T>(endpoint: string, headers: HttpHeaders = this.apiHeader, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.get<T>(this.buildApiUrl(endpoint), { headers: headers }).pipe(timeout(callTimeout));
    }

    public Post<T>(endpoint: string, body?: any, headers: HttpHeaders = this.apiHeader, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.post<T>(this.buildApiUrl(endpoint), body, { headers: headers }).pipe(timeout(callTimeout));
    }

    public async GetAsset(file: string) {
        let results: any = null;

        try {
            let stream$ = this.http.get(`assets/${file}`);

            results = await lastValueFrom(stream$);
        } catch (error: any) {
            throw error;
        }

        return results;
    }

    public GetExternal<T>(url: string, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.get<T>(url).pipe(timeout(callTimeout));
    }

    // ======== Wells API Query Methods ========
    public query(payload: any): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const queryUrl = this.appconfigService.concatenateUrl(this.config.wssUrl, '/api/v1/query');
                return this.http.post<any>(queryUrl, payload, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                if (error.status === 401) {
                    this.clearWellsToken();
                    this.router.navigate(['/']);
                }
                throw error;
            })
        );
    }

    public getConversations(): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                return this.http.get<any>(this.buildApiUrl('/api/v1/query/conversations'), { headers }).pipe(timeout(TIMEOUT));
            }),
            map((response: any) => {
                // Extract sessions array from response if it's wrapped
                return Array.isArray(response) ? response : (response?.sessions || []);
            }),
            catchError(error => {
                if (error.status === 401) {
                    this.clearWellsToken();
                    this.router.navigate(['/']);
                }
                throw error;
            })
        );
    }

    public getConversationById(sessionId: string): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                return this.http.get<any>(this.buildApiUrl(`/api/v1/query/conversations/${sessionId}`), { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                if (error.status === 401) {
                    this.clearWellsToken();
                    this.router.navigate(['/']);
                }
                throw error;
            })
        );
    }

    public clearSession(sessionId: string): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                return this.http.post<any>(this.buildApiUrl('/api/v1/query/clear'), { session_id: sessionId }, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                if (error.status === 401) {
                    this.clearWellsToken();
                    this.router.navigate(['/']);
                }
                throw error;
            })
        );
    }

    public submitFeedback(feedback: any): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                return this.http.post<any>(this.buildApiUrl('/api/v1/feedback'), feedback, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                if (error.status === 401) {
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    // ======== Wells Data API Methods ========
    /**
     * GET /api/v1/wells-summary/{well_id} for single well
     * POST /api/v1/wells-summary for multiple wells
     * Accepts either a single well ID (string) or array of well IDs
     */
    public getWellsSummary(wellId: string | string[]): Observable<any> {
        // Convert single well to array format
        const wellIds = Array.isArray(wellId) ? wellId : [wellId];
        
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const payload = { well_ids: wellIds };
                return this.http.post<any>(this.buildApiUrl('/api/v1/wells-summary'), payload, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * POST /api/v1/performance-summary with well_ids array in payload
     * Accepts either a single well ID (string) or array of well IDs
     */
    public getPerformanceSummary(wellId: string | string[]): Observable<any> {
        // Convert single well to array format
        const wellIds = Array.isArray(wellId) ? wellId : [wellId];
        
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const payload = { well_ids: wellIds };
                return this.http.post<any>(this.buildApiUrl('/api/v1/performance-summary'), payload, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * POST /api/v1/npt-summary with well_ids array in payload
     * Accepts either a single well ID (string) or array of well IDs
     */
    public getNptSummary(wellId: string | string[]): Observable<any> {
        // Convert single well to array format
        const wellIds = Array.isArray(wellId) ? wellId : [wellId];
        
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const payload = { well_ids: wellIds };
                return this.http.post<any>(this.buildApiUrl('/api/v1/npt-summary'), payload, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * POST /api/v1/daily-report with well_ids and job_ids arrays in payload
     * Returns daily report entries (ReportStartDate, Summary) per well
     */
    public getDailyReport(wellIds: string[], jobIds: string[]): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const payload = { well_ids: wellIds, job_ids: jobIds };
                return this.http.post<any>(this.buildApiUrl('/api/v1/daily-report'), payload, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * POST /api/v1/time-log with well_ids and job_ids arrays in payload
     * Accepts either a single well ID + job ID, or array of well IDs + job IDs
     */
    public getTimeLog(wellId: string | string[], jobId?: string | string[]): Observable<any> {
        // Convert single well to array format
        const wellIds = Array.isArray(wellId) ? wellId : [wellId];
        const jobIds = jobId ? (Array.isArray(jobId) ? jobId : [jobId]) : [];
        
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const payload = { 
                    well_ids: wellIds, 
                    job_ids: jobIds
                };
                return this.http.post<any>(this.buildApiUrl('/api/v1/time-log'), payload, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * POST /api/v1/causal-summary with well_ids array in payload
     * Accepts either a single well ID (string) or array of well IDs
     */
    public getCausalSummary(wellId: string | string[]): Observable<any> {
        // Convert single well to array format
        const wellIds = Array.isArray(wellId) ? wellId : [wellId];
        
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const payload = { well_ids: wellIds };
                return this.http.post<any>(this.buildApiUrl('/api/v1/causal-summary'), payload, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    // ======== Multi-Well API Methods (POST-based) ========

    /**
     * GET /api/v1/wells-list?field_name=X for single field
     * GET /api/v1/wells-list for multiple fields (POST with array in body)
     * Returns list of wells for a given field or multiple fields.
     */
    public getWellsList(fieldName: string | string[]): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                
                // If single field, use GET with query parameter
                if (typeof fieldName === 'string') {
                    const url = this.buildApiUrl(`/api/v1/wells-list?field_name=${encodeURIComponent(fieldName)}`);
                    return this.http.get<any>(url, { headers }).pipe(timeout(TIMEOUT));
                } 
                // If multiple fields, use POST with array in body
                else {
                    const url = this.buildApiUrl('/api/v1/wells-list');
                    const payload = { field_names: fieldName };  // Array of field names
                    return this.http.post<any>(url, payload, { headers }).pipe(timeout(TIMEOUT));
                }
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    public getFieldsList(): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const url = this.buildApiUrl('/api/v1/fields');
                return this.http.get<any>(url, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    //  Wells API Authentication Methods
    public issueWellsToken(email: string = 'test@example.com'): Observable<any> {
        // Get the MSAL access token from the authenticated user
        const account = this.msalService.instance.getActiveAccount();
        
        if (!account) {
            return throwError(() => new Error('No user authenticated. Please log in with MSAL first.'));
        }
        
        // Acquire token silently from MSAL
        const tokenRequest = {
            scopes: [`${this.config.clientId}/user_impersonation`],
            account: account
        };
        
        return new Observable(observer => {
            this.msalService.acquireTokenSilent(tokenRequest).subscribe({
                next: (response: any) => {
                    const msalJwtToken = response.accessToken;
                    
                    // Store token in localStorage for fallback
                    localStorage.setItem('wells_api_token', msalJwtToken);
                    this.tokenSubject.next(msalJwtToken);

                    // Avoid calling Graph memberOf on every API request.
                    if (this.rolesLoaded) {
                        observer.next({
                            access_token: msalJwtToken,
                            roles: this.rolesCache
                        });
                        return;
                    }
                    
                    // Now fetch user roles from Graph API
                    this.fetchUserRolesFromGraph(msalJwtToken).subscribe({
                        next: (roles: any) => {
                            this.rolesLoaded = true;
                            this.rolesCache = Array.isArray(roles) ? roles : [];
                            observer.next({ 
                                access_token: msalJwtToken,
                                roles: this.rolesCache 
                            });
                        },
                        error: (error: any) => {
                            this.rolesLoaded = true;
                            this.rolesCache = [];
                            observer.next({ 
                                access_token: msalJwtToken,
                                roles: [] 
                            });
                        }
                    });
                },
                error: (error: any) => {
                    observer.error(new Error('Failed to acquire MSAL token: ' + error.message));
                }
            });
        });
    }

    private fetchUserRolesFromGraph(msalJwtToken: string): Observable<any> {
        return this.graphService.getMemberOf().pipe(
            map((groups: any[]) =>
                groups
                    .filter((entry: any) => entry.displayName)
                    .map((entry: any) => ({
                        id: entry.id,
                        displayName: entry.displayName,
                        type: entry['@odata.type']
                    }))
            ),
            catchError((error: any) => throwError(() => error))
        );
    }

    public setWellsToken(token: string): void {
        this.tokenSubject.next(token);
        localStorage.setItem('wells_api_token', token);
    }

    public getWellsToken(): string | null {
        // First check tokenSubject, then localStorage
        return this.tokenSubject.value || localStorage.getItem('wells_api_token') || null;
    }

    public clearWellsToken(): void {
        this.tokenSubject.next(null);
        localStorage.removeItem('wells_api_token');
    }

   
    public getLookbackAggregate(wellIds: string[], includeSections: string[]): Observable<any> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const payload = {
                    well_ids: wellIds,
                    include_sections: includeSections
                };
                return this.http.post<any>(this.buildApiUrl('/api/v1/lookback-aggregate'), payload, { headers }).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * Fetch lookback lessons for one or more wells
     * @param wellIds - Array of well IDs to analyze
     * @returns Observable with Lessons Learnt report
     */
    public getLessonsLearned(wellIds: string[]): Observable<LookbackLessonsResponse> {
        return this.issueWellsToken().pipe(
            switchMap((tokenResponse: any) => {
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.access_token}`,
                    'Content-Type': 'application/json',
                });
                const payload = {
                    well_ids: wellIds
                };
                return this.http.post<LookbackLessonsResponse>(
                    this.buildApiUrl('/api/v1/lookbacklessons'),
                    payload,
                    { headers }
                ).pipe(timeout(TIMEOUT));
            }),
            catchError(error => {
                throw error;
            })
        );
    }
}
